import { rostersFor, SC2_MODES, SC2_ROSTERS, type SC2Context } from "@/games/sc2/sc2-registry";

interface CommandCardProps {
  mode: string;
  /** The selected roster id: a Race in MP, a campaign Edition (WoL/HotS/LotV/NCO), a Commander in co-op. */
  roster: string;
  context: string;
  /** Contexts offered for the current roster and mode, already filtered (contextsFor). */
  contexts: SC2Context[];
  onMode: (id: string) => void;
  onRoster: (id: string) => void;
  onContext: (id: string) => void;
}

const RACE_LABEL: Record<string, string> = { terran: "Terran", zerg: "Zerg", protoss: "Protoss" };

/** Mode, Race/Commander, and selected unit/structure pickers; the selection drives the command card. */
export function CommandCard({
  mode,
  roster,
  context,
  contexts,
  onMode,
  onRoster,
  onContext,
}: CommandCardProps) {
  // a build submenu is not in the unit dropdown; show its worker as the dropdown value
  // and a breadcrumb + Back to the worker card
  const activeCtx = SC2_ROSTERS[roster]?.contexts.find((c) => c.id === context);
  const submenu = activeCtx?.parent ? activeCtx : null;
  const unitValue = submenu?.parent ?? context;
  // section the unit dropdown into Units / Structures, mirroring the
  // Standard/Split keyboard picker: the leading kind-less option ("nothing") stays ungrouped.
  const ungrouped = contexts.filter((c) => !c.kind);
  const units = contexts.filter((c) => c.kind === "unit");
  const structures = contexts.filter((c) => c.kind === "building");
  // the second axis: a flat list (races in MP, the four campaign Editions in campaign), or the 18
  // commanders grouped by faction in co-op
  const rosters = rostersFor(mode);
  const coop = mode === "coop";
  const byRace = (race: string) => rosters.filter((r) => r.race === race);
  return (
    <span className="command-card">
      <span className="command-card-label">Command Card</span>
      <select aria-label="Mode" value={mode} onChange={(e) => onMode(e.target.value)}>
        {SC2_MODES.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
      <span className="command-card-sep">›</span>
      <select
        aria-label={coop ? "Commander" : mode === "campaign" ? "Campaign" : "Race"}
        value={roster}
        onChange={(e) => onRoster(e.target.value)}
      >
        {coop
          ? ["terran", "zerg", "protoss"].map((race) => (
              <optgroup key={race} label={RACE_LABEL[race]}>
                {byRace(race).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </optgroup>
            ))
          : rosters.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
      </select>
      <span className="command-card-sep">›</span>
      <select
        aria-label="Selected unit"
        value={unitValue}
        onChange={(e) => onContext(e.target.value)}
      >
        {ungrouped.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
        {units.length > 0 ? (
          <optgroup label="Units">
            {units.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </optgroup>
        ) : null}
        {structures.length > 0 ? (
          <optgroup label="Structures">
            {structures.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      {submenu ? (
        <>
          <span className="command-card-sep">›</span>
          <span className="command-card-submenu">{submenu.label}</span>
          <button
            type="button"
            className="command-card-back"
            aria-label="Back to unit card"
            onClick={() => submenu.parent && onContext(submenu.parent)}
          >
            ‹ Back
          </button>
        </>
      ) : null}
    </span>
  );
}
